import { AlertKey } from './types';

const isStudyPendingForApproval = (study: Study): boolean => {
  return study.state === 'pending' && study.pending_reasons?.includes('approval');
};

const hasValidApprovalRequest = (hasApprovalRequest: boolean, approvalRequest?: ApprovalRequest): boolean => {
  return hasApprovalRequest && !!approvalRequest;
};

const isUserCreator = (approvalRequest: ApprovalRequest, userId?: number): boolean => {
  return approvalRequest.creator_id === userId;
};

const isUserApprover = (approvalRequest: ApprovalRequest, userId?: number): boolean => {
  return approvalRequest.approver_id === userId;
};

const isUserOther = (approvalRequest: ApprovalRequest, userId?: number): boolean => {
  return !isUserCreator(approvalRequest, userId) && !isUserApprover(approvalRequest, userId);
};

export const getAlerts = (params: {
  study: Study;
  slots: StudySlotsBalance;
  canFund: boolean;
  canApprove?: boolean;
  hasApprovalRequest?: boolean;
  approvalRequest?: ApprovalRequest;
  userId?: number;
}): Record<AlertKey, boolean> => {
  const { study, slots, canFund, canApprove = false, hasApprovalRequest = false, approvalRequest, userId } = params;
  const active = study.state === 'active';
  const tremendous = study.incentive_method === 'tremendous';
  const { target, limit_reached, completed, enough_slots } = slots;
  const filled = active && target > 0 && limit_reached && completed;
  const has_calendar = study.is_nylas && !study.focus_group;

  const isPendingForApproval = isStudyPendingForApproval(study);
  const hasValidRequest = hasValidApprovalRequest(hasApprovalRequest, approvalRequest);

  return {
    batching_on: !filled && study.batch_on && !!study.next_batch_at,
    must_fund: active && tremendous && study.funding.usd_outstanding > 0,
    filled,
    cal_slots: Boolean(has_calendar && active && !limit_reached && !enough_slots),
    good_cal: Boolean(has_calendar && active && !limit_reached && enough_slots),
    pending_funding: tremendous && study.state === 'pending' && !canFund,
    pending_approval: isPendingForApproval && !canApprove && !hasApprovalRequest,
    approval_requested: isPendingForApproval && hasValidRequest && isUserCreator(approvalRequest!, userId),
    admin_approval_requested: isPendingForApproval && hasValidRequest && isUserApprover(approvalRequest!, userId),
    other_approval_requested: isPendingForApproval && hasValidRequest && isUserOther(approvalRequest!, userId),
    funding_required: tremendous && study.state === 'pending' && canFund && study.funding.usd_outstanding > 0,
    sample: !!study.sample,
    edit_scheduled_events: study.style === 'video_call' && study.state === 'active'
  };
};
